import React from 'react';
import { toast } from 'react-toastify';
import { setVisibleUsers } from '../../../../../../../actions/ui';
import {
    deleteUserItem,
    getUserList,
    resendUserInvitation,
    searchUserList,
    updateUserItem,
} from '../../../../../../../helpers/user-helper';
import { getGroup } from '../../../../../../../helpers/group-helper';

export const useSettingsUsersManagement = ({ token, dispatch }) => {
    const [users, setUsers] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [savingUserId, setSavingUserId] = React.useState('');
    const [searchField, setSearchField] = React.useState('email');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState('ALL');
    const [offset, setOffset] = React.useState(0);
    const [limit, setLimit] = React.useState(10);
    const [total, setTotal] = React.useState(0);
    const [hasNext, setHasNext] = React.useState(false);
    const [hasPrev, setHasPrev] = React.useState(false);
    const [confirmState, setConfirmState] = React.useState({
        open: false,
        user: null,
        nextActive: true,
    });

    const loadUsers = React.useCallback(async () => {
        if (!token) {
            setError('No se pudo validar la sesión.');
            setUsers([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        const params = {
            limit,
            offset,
            includeDeleted: true,
        };

        if (statusFilter !== 'ALL') {
            params.status = statusFilter;
        }

        const usersRequest = searchTerm.trim()
            ? searchUserList(token, {
                ...params,
                field: searchField,
                value: searchTerm.trim(),
            })
            : getUserList(token, params);

        const groupsRequest = getGroup(token, {
            includeDeleted: false,
            limit: 500,
            offset: 0,
        });

        const [resp, groupsResp] = await Promise.all([usersRequest, groupsRequest]);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo obtener el listado de usuarios.';
            setError(message);
            toast.error(message);
            setUsers([]);
            setTotal(0);
            setHasNext(false);
            setHasPrev(false);
            setIsLoading(false);
            return;
        }

        const nextGroupsByUser = {};
        if (groupsResp?.success && Array.isArray(groupsResp?.data)) {
            groupsResp.data.forEach((group) => {
                const groupName = String(group?.name || '').trim();
                if (!groupName || !Array.isArray(group?.users)) {
                    return;
                }

                group.users.forEach((groupUser) => {
                    const userId = String(groupUser?._id || groupUser || '');
                    if (!userId) {
                        return;
                    }

                    if (!Array.isArray(nextGroupsByUser[userId])) {
                        nextGroupsByUser[userId] = [];
                    }

                    nextGroupsByUser[userId].push(groupName);
                });
            });
        }

        const nextUsers = Array.isArray(resp.data) ? resp.data : [];
        const normalizedUsers = nextUsers.map((user) => ({
            ...user,
            groups: nextGroupsByUser[String(user?._id || '')] || [],
        }));

        setUsers(normalizedUsers);
        setTotal(Number(resp.total || 0));
        setHasNext(Boolean(resp.hasNext));
        setHasPrev(Boolean(resp.hasPrev));
        setIsLoading(false);
    }, [token, limit, offset, statusFilter, searchField, searchTerm]);

    React.useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    React.useEffect(() => {
        dispatch(setVisibleUsers(users));
    }, [dispatch, users]);

    React.useEffect(() => {
        return () => {
            dispatch(setVisibleUsers([]));
        };
    }, [dispatch]);

    const openToggleDialog = React.useCallback((user) => {
        if (!user?._id || savingUserId) {
            return;
        }

        setConfirmState({
            open: true,
            user,
            nextActive: !Boolean(user.active),
        });
    }, [savingUserId]);

    const closeToggleDialog = React.useCallback(() => {
        if (savingUserId) {
            return;
        }

        setConfirmState({
            open: false,
            user: null,
            nextActive: true,
        });
    }, [savingUserId]);

    const handleToggleActive = React.useCallback(async () => {
        const user = confirmState.user;
        const nextActive = confirmState.nextActive;

        if (!token || !user?._id || savingUserId) {
            return;
        }

        const actionText = nextActive ? 'activar' : 'desactivar';

        setSavingUserId(user._id);
        setError('');

        const resp = nextActive
            ? await updateUserItem(token, user._id, { active: true })
            : await deleteUserItem(token, user._id);

        if (!resp?.success) {
            const message = resp?.message || `No se pudo ${actionText} el usuario.`;
            setError(message);
            toast.error(message);
            setSavingUserId('');
            return;
        }

        toast.success(resp?.message || `Usuario ${nextActive ? 'activado' : 'desactivado'} correctamente.`);
        setSavingUserId('');
        closeToggleDialog();
        await loadUsers();
    }, [token, confirmState, savingUserId, closeToggleDialog, loadUsers]);

    const handleResendInvitation = React.useCallback(async (user) => {
        if (!token || !user?._id || savingUserId) {
            return;
        }

        setSavingUserId(user._id);
        const resp = await resendUserInvitation(token, user._id);

        if (!resp?.success) {
            toast.error(resp?.message || 'No se pudo reenviar la invitación.');
            setSavingUserId('');
            return;
        }

        toast.success(resp?.message || 'Invitación reenviada exitosamente.');
        setSavingUserId('');
    }, [token, savingUserId]);

    const handleClearFilters = React.useCallback(() => {
        setSearchField('email');
        setSearchTerm('');
        setStatusFilter('ALL');
        setOffset(0);
    }, []);

    const handleRowsPerPageChange = React.useCallback((nextLimit) => {
        setLimit(Number(nextLimit));
        setOffset(0);
    }, []);

    return {
        users,
        isLoading,
        error,
        savingUserId,
        searchField,
        setSearchField,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        offset,
        setOffset,
        limit,
        total,
        hasNext,
        hasPrev,
        confirmState,
        openToggleDialog,
        closeToggleDialog,
        handleToggleActive,
        handleResendInvitation,
        handleClearFilters,
        handleRowsPerPageChange,
    };
};
